import React, { useCallback, useMemo } from 'react';
import { CenteredModal, SubmitButton, TextSelect } from 'components';
import { useTranslation } from 'react-i18next';
import { useToast } from '@app/core';
import {
  Participation,
  ParticipationReportReason,
  ParticipationUpdateData,
  SelectionPayload,
} from '@app/api';
import { Form, Formik } from 'formik';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import * as Yup from 'yup';
import { MutationOptions } from 'react-query';
import { useSubmitReportReason } from '../hooks/useSubmitReportReason';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 10,
  },
  textAreaContainer: {
    marginTop: 0,
    marginBottom: 0,
  },
  submitButton: {
    width: 'fit-content',
    justifySelf: 'right',
    alignSelf: 'end',
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose: () => any;
  participation?: Participation | Participation[];
  selectionPayload?: SelectionPayload;
  sessionId: string;
  onSuccess?: () => any;
  mutationOptions?: MutationOptions<
    Participation,
    Error,
    Participation,
    Participation | ParticipationUpdateData
  >;
}

interface Values {
  score: number;
  message: string | undefined;
  reportReason: ParticipationReportReason | undefined;
}

const initialValues: Values = {
  score: -100,
  message: undefined,
  reportReason: undefined,
};

const validationSchema = Yup.object().shape({
  reportReason: Yup.string()
    .required()
    .oneOf(Object.values(ParticipationReportReason)),
});

export const UnmoderatedReportReasonModal = ({
  participation,
  selectionPayload,
  isOpen,
  onRequestClose,
  sessionId,
  onSuccess,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();

  const participations = useMemo(() => {
    if (selectionPayload) {
      return selectionPayload.selectedParticipations || [];
    }
    if (!participation) {
      return [];
    }
    return Array.isArray(participation) ? participation : [participation];
  }, [participation, selectionPayload]);

  const submitReportReason = useSubmitReportReason(
    sessionId,
    selectionPayload,
    participations,
  );

  const ParticipationReportReasonOptions: Array<{
    value: ParticipationReportReason;
    label: string;
  }> = useMemo(
    () =>
      [
        ParticipationReportReason.PROFILE_LIE,
        ParticipationReportReason.NOT_SERIOUS_ANSWERS,
      ].map((reason) => ({
        value: reason,
        label: t(
          `sessions.details.monitoring.meetingRating.participationReport.participationReportReason.${reason}`,
        ),
      })),
    [t],
  );

  const submit = useCallback(
    async (values: Values) => {
      try {
        if (!values.reportReason) {
          return;
        }
        await submitReportReason({
          reportReason: values.reportReason,
          message: values.message,
        });
        onRequestClose();
        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        toast.exception(error);
      }
    },
    [submitReportReason, onRequestClose, onSuccess, toast],
  );

  return (
    <CenteredModal
      title={t(
        'sessions.details.monitoring.meetingRating.participationReport.label_unmoderated',
      )}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={submit}
        validationSchema={validationSchema}
      >
        {({ values }) => (
          <Form className={classes.container}>
            <TextSelect
              noMargin
              name="reportReason"
              options={ParticipationReportReasonOptions}
              label={t(
                'sessions.details.monitoring.meetingRating.participationReport.label',
              )}
              placeholder={t(
                'sessions.details.monitoring.meetingRating.participationReport.placeholder',
              )}
            />
            <SubmitButton
              className={classes.submitButton}
              disabled={!values.reportReason}
              text={t('sessions.details.monitoring.meetingRating.submitRating')}
            />
          </Form>
        )}
      </Formik>
    </CenteredModal>
  );
};
